/**
 * Executive Asks — decisions required, ranked, with rationale. A status change
 * (open → accepted / deferred) goes through decide_executive_ask, which
 * requires a rationale and appends a governance event.
 */

import { useState } from 'react';
import { Plus, Pencil, ClipboardList } from 'lucide-react';
import type {
  AssembledPor,
  PorExecutiveAsk,
  PorAskDecisionType,
  PorAskStatus,
} from '../../types/plan-of-record';
import {
  createExecutiveAsk,
  updateExecutiveAsk,
  deleteExecutiveAsk,
  decideExecutiveAsk,
} from '../../lib/planOfRecordService';
import {
  ASK_DECISION_TYPE_LABELS,
  AskStatusPill,
  GhostButton,
  PorCard,
  PorCardHeader,
  PorEmptyState,
  PorModal,
  PrimaryButton,
  RationaleModal,
} from './porUi';
import { PorNarrativeMarkdown } from './PorNarrativeMarkdown';

const DECISION_TYPES: PorAskDecisionType[] = ['approve', 'resource', 'authorize', 'sequence', 'confirm'];

export function ExecutiveAsksPanel({
  assembled,
  refresh,
}: {
  assembled: AssembledPor;
  refresh: () => Promise<void>;
}) {
  const [editing, setEditing] = useState<PorExecutiveAsk | 'new' | null>(null);
  const [deciding, setDeciding] = useState<{ ask: PorExecutiveAsk; to: PorAskStatus } | null>(null);
  const asks = assembled.executive_asks;

  return (
    <PorCard>
      <PorCardHeader
        title="Executive Asks"
        count={asks.length}
        hint="Decisions required — ranked."
        action={
          <GhostButton onClick={() => setEditing('new')}>
            <span className="inline-flex items-center gap-1">
              <Plus className="h-3.5 w-3.5" /> Add
            </span>
          </GhostButton>
        }
      />

      {asks.length === 0 ? (
        <PorEmptyState
          icon={<ClipboardList className="h-6 w-6" />}
          title="No executive asks"
          prompt="What decisions does leadership need to make for this plan to move? Rank them, give each a rationale."
          action={<GhostButton onClick={() => setEditing('new')}>Add an ask</GhostButton>}
        />
      ) : (
        <div className="divide-y divide-[var(--rule)]">
          {asks.map((ask) => (
            <div key={ask.id} className="py-3 flex items-start gap-3 group">
              <span className="font-mono text-xs text-[var(--text-ter)] mt-0.5 w-5 flex-shrink-0">
                #{ask.rank}
              </span>
              <div className="flex-1 min-w-0 space-y-1.5">
                <PorNarrativeMarkdown source={ask.description} />
                <div className="flex items-center gap-2 flex-wrap">
                  <AskStatusPill status={ask.status} />
                  <span className="text-[11px] font-mono uppercase text-[var(--text-ter)]">{ASK_DECISION_TYPE_LABELS[ask.decision_type] ?? ask.decision_type}</span>
                </div>
                {ask.rationale.trim().length > 0 && (
                  <>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] pt-0.5">
                      Rationale
                    </p>
                    <PorNarrativeMarkdown source={ask.rationale} />
                  </>
                )}
                {ask.status === 'open' && (
                  <div className="flex items-center gap-2 mt-1.5">
                    <button
                      onClick={() => setDeciding({ ask, to: 'approved' })}
                      className="text-xs font-medium text-primary-700 hover:text-primary-800"
                    >
                      Accept
                    </button>
                    <span className="text-[var(--text-ter)]">·</span>
                    <button
                      onClick={() => setDeciding({ ask, to: 'deferred' })}
                      className="text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                    >
                      Defer
                    </button>
                  </div>
                )}
                {ask.status !== 'open' && (
                  <button
                    onClick={() => setDeciding({ ask, to: 'open' })}
                    className="text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-sec)] mt-1.5"
                  >
                    Reopen
                  </button>
                )}
              </div>
              <button
                onClick={() => setEditing(ask)}
                className="text-[var(--text-ter)] hover:text-[var(--text-sec)] opacity-0 group-hover:opacity-100 transition-opacity mt-0.5"
                aria-label="Edit ask"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <AskModal
          porId={assembled.id}
          nextRank={asks.length + 1}
          ask={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      )}

      {deciding && (
        <RationaleModal
          title={
            deciding.to === 'approved'
              ? 'Accept executive ask'
              : deciding.to === 'deferred'
                ? 'Defer executive ask'
                : 'Reopen executive ask'
          }
          subtitle="This decision is recorded in the immutable Change Log."
          confirmLabel="Record decision"
          onClose={() => setDeciding(null)}
          onConfirm={async (rationale) => {
            const { error } = await decideExecutiveAsk(deciding.ask.id, deciding.to, rationale);
            if (error) throw new Error(error);
            setDeciding(null);
            await refresh();
          }}
        >
          <p className="text-sm text-[var(--text-sec)] mb-3">“{deciding.ask.description}”</p>
        </RationaleModal>
      )}
    </PorCard>
  );
}

function AskModal({
  porId,
  ask,
  nextRank,
  onClose,
  onDone,
}: {
  porId: string;
  ask: PorExecutiveAsk | null;
  nextRank: number;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [description, setDescription] = useState(ask?.description ?? '');
  const [rationale, setRationale] = useState(ask?.rationale ?? '');
  const [rank, setRank] = useState<string>(String(ask?.rank ?? nextRank));
  const [decisionType, setDecisionType] = useState<PorAskDecisionType>(ask?.decision_type ?? 'approve');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = description.trim().length > 0 && rationale.trim().length > 0;

  const handleSave = async () => {
    if (!valid) {
      setError('Description and rationale are both required.');
      return;
    }
    setBusy(true);
    setError(null);
    const payload = {
      description: description.trim(),
      rationale: rationale.trim(),
      rank: Math.max(1, Number(rank) || nextRank),
      decision_type: decisionType,
    };
    const { error: err } = ask
      ? await updateExecutiveAsk(ask.id, payload)
      : await createExecutiveAsk(porId, payload);
    if (err) {
      setError(err);
      setBusy(false);
      return;
    }
    setBusy(false);
    await onDone();
  };

  const handleDelete = async () => {
    if (!ask) return;
    setBusy(true);
    const { error: err } = await deleteExecutiveAsk(ask.id);
    if (err) {
      setError(err);
      setBusy(false);
      return;
    }
    setBusy(false);
    await onDone();
  };

  const field = 'w-full text-sm  border border-[var(--rule)] px-3 py-2 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none';
  const label = 'block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1.5';

  return (
    <PorModal
      title={ask ? 'Edit executive ask' : 'Add executive ask'}
      onClose={onClose}
      footer={
        <>
          {ask && (
            <button
              onClick={handleDelete}
              disabled={busy}
              className="mr-auto text-sm font-medium text-luminous-coral-700 hover:text-luminous-coral-800 disabled:opacity-40"
            >
              Delete
            </button>
          )}
          <GhostButton onClick={onClose} disabled={busy}>
            Cancel
          </GhostButton>
          <PrimaryButton onClick={handleSave} disabled={busy || !valid}>
            {busy ? 'Saving…' : 'Save'}
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className={label}>The ask</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className={`${field} resize-none`}
            autoFocus
          />
        </div>
        <div>
          <label className={label}>Rationale</label>
          <textarea
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            rows={2}
            className={`${field} resize-none`}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={label}>Rank</label>
            <input
              type="number"
              min={1}
              value={rank}
              onChange={(e) => setRank(e.target.value)}
              className={field}
            />
          </div>
          <div>
            <label className={label}>Decision type</label>
            <select
              value={decisionType}
              onChange={(e) => setDecisionType(e.target.value as PorAskDecisionType)}
              className={field}
            >
              {DECISION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {ASK_DECISION_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>
        </div>
        {error && <p className="text-xs text-luminous-coral-700">{error}</p>}
      </div>
    </PorModal>
  );
}
